import { useEffect, useState } from 'react';

/**
 * Track a host element matching `selector`, updating as the DOM changes.
 *
 * With `once = false` the MutationObserver stays live for the component's
 * lifetime, so the returned node follows host re-renders. Host frameworks
 * (Magento/Knockout) briefly detach a mount node while re-rendering, which
 * makes `querySelector` momentarily miss — we keep the last node while it's
 * still connected instead of nulling it, so a portaled subtree isn't torn
 * down and remounted mid-interaction (the add-to-bag click race). The node is
 * only cleared when it's truly gone, and only swapped when actually replaced.
 */
export const useElementObserver = (selector: string, once = true) => {
  const [element, setElement] = useState<Element | null>(null);

  useEffect(() => {
    const tryFind = () => {
      const el = document.querySelector(selector);
      setElement((prev) => {
        if (el) return el;
        return prev?.isConnected ? prev : null;
      });
      return !!el;
    };

    if (tryFind() && once) return;

    const observer = new MutationObserver(() => {
      const found = tryFind();
      if (found && once) {
        observer.disconnect();
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => observer.disconnect();
  }, [selector, once]);

  return element;
};
